import React, { useMemo, useState } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Autocomplete, Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useCanCreateDeal } from "hooks";
import { usePrompt } from "hooks/shared-hooks/use-prompt-hook";
import { useDealActions, useTaskDealsSelect } from "store";
import { DealBase, ISuggestion } from "store/types";

import { CreateDealContent } from "./CreateDealContent";

interface Props {
  onCancel: () => void;
  onSubmit: (suggestion: ISuggestion) => void;
  matchedString?: string;
}

export const SelectDealContent: React.FC<Props> = ({ onCancel, onSubmit, matchedString }) => {
  const canCreateDeal = useCanCreateDeal();
  const openPrompt = usePrompt();
  const { getDealFromFileNameAction } = useDealActions();
  const deals = useTaskDealsSelect();
  const [deal, setDeal] = useState<DealBase | null>(null);
  const [dealNotFound, setDealNotFound] = useState<boolean>(false);
  const [localDeals, setLocalDeals] = useState<DealBase[]>(deals ?? []);

  const { action: getDealFromFileName, loading } = useActionHook(
    () => getDealFromFileNameAction({ file_name: matchedString || "" }),
    {
      onSuccess: (deal) => {
        deal?.id ? setDeal(deal) : setDealNotFound(true);
      }
    }
  );

  const onCreateDeal = async () => {
    const data = await openPrompt({
      Component: CreateDealContent
    });
    if (data) {
      setLocalDeals([...localDeals, { id: data.dealId, title: data.dealTitle } as DealBase]);
      setDeal({ id: data.dealId, title: data.dealTitle } as DealBase);
      setDealNotFound(false);
    }
  };

  const selectedOption = useMemo(() => {
    return localDeals?.find((option) => option.id === deal?.id);
  }, [localDeals, deal]);

  return (
    <Box sx={{ ...style }}>
      <Box>
        <Typography sx={{ fontSize: "16px", color: "#0a3142", marginBottom: "5px" }}>Select deal</Typography>
        <Stack gap="20px" mb="20px">
          <Stack sx={{ backgroundColor: "#dae0e2", height: "1px", marginBottom: "14px" }} />
          <Stack direction="row" gap="10px" alignItems="center">
            <Autocomplete
              key={selectedOption?.id}
              disableClearable
              sx={{ width: 356 }}
              getOptionLabel={(option) => option.title}
              options={localDeals}
              value={selectedOption || undefined}
              onChange={(_, option) => {
                option && setDeal(option);
                setDealNotFound(false);
              }}
              renderOption={(props, item) => (
                <li {...props} key={item.id}>
                  {item.title}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  sx={{
                    ".MuiFormHelperText-root": {
                      bottom: "-17px",
                      position: "absolute",
                      fontSize: "11px",
                      marginLeft: "0px"
                    }
                  }}
                  {...params}
                  placeholder="Select"
                  label="Deal"
                  error={dealNotFound}
                  helperText={dealNotFound && "No matching Deal found. Select a Deal or create a new one"}
                />
              )}
              PaperComponent={({ children }) => {
                return (
                  <Paper
                    sx={{
                      position: "absolute",
                      backgroundColor: "white",
                      zIndex: "10",
                      width: "100%",
                      borderRadius: "10px",
                      "& .MuiAutocomplete-option:hover": {
                        backgroundColor: "#edf1f9"
                      }
                    }}
                  >
                    <>
                      {children}
                      {canCreateDeal && (
                        <Button
                          onMouseDown={onCreateDeal}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            height: "50px",
                            backgroundColor: "#fff",
                            boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                            borderRadius: "0 0 10px 10px"
                          }}
                          fullWidth
                        >
                          Add Deal
                        </Button>
                      )}
                    </>
                  </Paper>
                );
              }}
            />
            {matchedString && (
              <MaterialTooltip title="Fill deal from file name" isShow>
                <IconButton size="sm" onClick={async () => await getDealFromFileName()} disabled={loading}>
                  <AutoFixHighIcon sx={{ width: "25px", height: "25px" }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </Stack>
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onCancel} sx={{ marginRight: "20px" }} variant="outlined">
            Close
          </Button>
          <Button disabled={!deal} onClick={() => deal && onSubmit({ id: deal.id, deal })} variant="contained">
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "8px",
  backgroundColor: "#fff",
  padding: "30px"
};
